import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import clsx from "clsx";
import { Image, ActionIcon, Group, Button } from "@mantine/core";
import { ReactComponent as ChevronLeftIcon } from "@/assets/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "@/assets/icons/chevron-right.svg";
import { ReactComponent as NewEditionIcon } from "@/assets/icons/new-edition.svg";
import { ReactComponent as BoughtIcon } from "@/assets/icons/bought.svg";
import { ReactComponent as DeleteIcon } from "@/assets/icons/delete.svg";
import {
  successNotification,
  cleanNotifications,
} from "@/helpers/notifications";
import { useCart } from "@/context/CartContext";
import { Issue, Publication } from "@/api/types";
import dayjs from "dayjs";
import "./SingleView.css";

interface SingleViewProps {
  type: "issue" | "publication";
  items: Issue[] | Publication[];
  myItems: Issue[] | Publication[];
  openFlipbook: (pdf: string) => void;
}

const isIssue = (item: Issue | Publication): item is Issue => {
  return "month" in item && "year" in item;
};

const SingleView: React.FC<SingleViewProps> = ({
  openFlipbook,
  items,
  myItems,
  type,
}) => {
  const navigate = useNavigate();
  const typeIsIssue = type === "issue";
  const [currentIndex, setIndex] = useState(0);
  const [item, setIssue] = useState(
    items.length > 0 ? items[currentIndex] : null
  );
  const [direction, setDirection] = useState(1);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;
  const isNew = (item: Issue | Publication) => {
    if (isIssue(item))
      return (
        item.month.toLowerCase() === dayjs().format("M") &&
        item.year === dayjs().format("YYYY")
      );
    else return dayjs(item.createdAt).format("M") === dayjs().format("M");
  };
  const { cart, addToCart, removeFromCart } = useCart();
  const formatMonth = (month: string) => {
    let formatted = dayjs(month).locale("hr").format("MMMM");
    formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    return formatted;
  };

  useEffect(() => {
    if (items.length > 0) {
      setIssue(items[currentIndex]);
    }
  }, [items, currentIndex]);

  const goToPrevious = () => {
    if (isFirst) return;
    else {
      setDirection(-1);
      const newIndex = currentIndex - 1;
      setIndex(newIndex);
      setIssue(items[newIndex]);
    }
  };

  const goToNext = () => {
    if (isLast) return;
    else {
      setDirection(1);
      const newIndex = currentIndex + 1;
      setIndex(newIndex);
      setIssue(items[newIndex]);
    }
  };

  const handleAddToCart = (item: Issue | Publication) => {
    addToCart({ ...item, cartId: `${type}-${item.id}`, type });
    successNotification({
      message: (
        <div className="add-to-cart-notification-group">
          <span>
            {typeIsIssue ? "Revija" : "Publikacija"} je dodana u košaricu!
          </span>
          <span
            className="add-to-cart-notification-link"
            onClick={() => {
              cleanNotifications();
              navigate("/cart");
            }}
          >
            Idi na košaricu
          </span>
        </div>
      ),
    });
  };

  const handleRemoveFromCart = (id: number) => {
    removeFromCart(`${type}-${id}`);
    successNotification({
      message: `${
        typeIsIssue ? "Revija" : "Publikacija"
      } je uklonjena iz košarice`,
    });
  };

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = (e: any) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: any) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const deltaX = touchEndX.current - touchStartX.current;
      if (deltaX > 50) {
        goToPrevious();
      } else if (deltaX < -50) {
        goToNext();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!item) return <div>Loading...</div>;

  return (
    <div className="single-container">
      <div className="flex px-4 items-center gap-[3rem]">
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          className={clsx("arrow-button", isFirst && "opacity-50")}
          disabled={isFirst}
          onClick={goToPrevious}
        >
          <ChevronLeftIcon fill="#0B1A29" />
        </ActionIcon>
        <div className="covers-group">
          {items[currentIndex - 2] && (
            <Image
              src={items[currentIndex - 2].cover.url}
              style={{ zIndex: 2 }}
              className="absolute single-image third from-left"
            />
          )}
          {items[currentIndex - 1] && (
            <Image
              src={items[currentIndex - 1].cover.url}
              style={{ zIndex: 3 }}
              className="absolute single-image second from-left"
            />
          )}
          <motion.img
            key={item.id}
            src={item.cover.url}
            initial={{
              x: direction * 20,
              scale: 0.95,
              opacity: 0.95,
            }}
            animate={{ x: 0, scale: 1, opacity: 1 }}
            exit={{ x: direction * -20, scale: 0.95, opacity: 0.95 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="relative single-image front"
            style={{ zIndex: 10 }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          {items[currentIndex + 1] && (
            <Image
              src={items[currentIndex + 1].cover.url}
              style={{ zIndex: 3 }}
              className="absolute single-image second from-right"
            />
          )}
          {items[currentIndex + 2] && (
            <Image
              src={items[currentIndex + 2].cover.url}
              style={{ zIndex: 2 }}
              className="absolute single-image third from-right"
            />
          )}
        </div>
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          className={clsx("arrow-button", isLast && "opacity-50")}
          disabled={isLast}
          onClick={goToNext}
        >
          <ChevronRightIcon className="text-[#0B1A29]" />
        </ActionIcon>
      </div>
      <div className="single-magazine-details">
        {isNew(item) && (
          <Group gap={10}>
            <NewEditionIcon />
            <div className="text-[#918865] text-sm font-bold">Novo izdanje</div>
          </Group>
        )}
        <Group justify="space-between" align="center">
          <div className="text-[#0b1a29] text-base font-bold">{item.title}</div>
          {!myItems?.map((item) => item.id).includes(item.id) ? (
            <div className="text-[#004a99] text-md font-bold">
              {item.price}€
            </div>
          ) : (
            <Group gap={5}>
              <BoughtIcon />
              <div className="text-[#515151] text-sm font-medium">Kupljeno</div>
            </Group>
          )}
        </Group>
        {isIssue(item) ? (
          <div className="text-[#515151] text-xs font-medium">
            {formatMonth(item.month)}, {item.year}
          </div>
        ) : (
          <div className="text-[#515151] text-xs font-medium">
            {item.author}
          </div>
        )}
        <div className="single-desc desk">{item.description}</div>
        <div className="single-buttons">
          {!myItems?.map((item) => item.id).includes(item.id) ? (
            cart.map((item) => item.cartId).includes(`${type}-${item.id}`) ? (
              <Button
                variant="filled"
                color="#004a99"
                radius="xl"
                size="md"
                className="single-button"
                leftSection={<DeleteIcon fill="white" />}
                onClick={() => handleRemoveFromCart(item.id)}
              >
                Ukloni iz košarice
              </Button>
            ) : (
              <Button
                variant="filled"
                color="#004a99"
                radius="xl"
                size="md"
                className="single-button"
                onClick={() => handleAddToCart(item)}
              >
                Dodaj u košaricu
              </Button>
            )
          ) : (
            <Button
              variant="outline"
              color="#004a99"
              radius="xl"
              size="md"
              className="single-button"
              onClick={() => openFlipbook(item.pdf.url)}
            >
              Pregledaj {typeIsIssue && "reviju"}
            </Button>
          )}
        </div>
        <div className="single-desc mob">{item.description}</div>
      </div>
    </div>
  );
};

export default SingleView;
